import { DialogPopupTypes, MediaContentDTO } from '@touche-lts/panel-ui/dist'
import { makeAutoObservable } from 'mobx'
import RootStore  from 'mobx-stores/root-store'
import { AddressDTO, StoreDTO } from 'models'
import { PaymentDetailsDTO } from 'models/payment-details'
import { MyStorePaths } from 'routes'
import { Api } from 'services'

export class ViewStorePageStore {
  private rootStore:any
  private storeApi: Api['storeApi']

  constructor (rootStore:any) {
    this.rootStore = rootStore
    this.storeApi = this.rootStore.generalStore.api.storeApi
    makeAutoObservable(this)
  }

  private storeBeforeChange: StoreDTO = null

  // OBSERVABLES
  public store: StoreDTO = null
  public storeDetailsEditMode: boolean = false

  // ACTIONS
  private setStore = (store: StoreDTO): void => {
    this.store = store
  }

  private setStoreDetailsEditMode = (value: boolean): void => {
    this.storeDetailsEditMode = value
  }

  // METHODS
  public clearStore = (): void => {
    this.setStore(null)
    this.storeBeforeChange = null
    this.setStoreDetailsEditMode(false)
    this.rootStore.storeAttributesStore.clearStore()
  }

  public onPageMount = async (storeId: number | string): Promise<void> => {
    const {
      storesMainStore: { getStoreById },
      storeAttributesStore: { onStoreAttributesSectionMount },
      storeShippingsStore: { onStoreShippingsSectionMount },
      routingStore: {
        location: { pathname },
        replace
      }
    } = this.rootStore

    if (pathname === '/') {
      replace(MyStorePaths.ROOT)
    }

    const store = await getStoreById(storeId)
    if (!store) return
    this.setStore(store)
    await onStoreAttributesSectionMount()
    onStoreShippingsSectionMount()
  }

  public setStoreProps = (props: Partial<StoreDTO>): void => {
    this.setStore({ ...this.store, ...props })
  }

  public onSuspendStoreClick = async (): Promise<void> => {
    const {
      generalStore: { openDialogPopup, closeDialogPopup, showSuccess },
      routingStore: { push }
    } = this.rootStore

    openDialogPopup({
      type: DialogPopupTypes.WARNING,
      message: `Are you sure you want to suspend "${this.store.name}" store?`,
      closeButtonLabel: 'No',
      buttons: [
        {
          children: 'Yes',
          preset: 'primary',
          onClick: async () => {
            const store = await this.storeApi.suspendStore(this.store.id)
            if (!store) {
              closeDialogPopup()
              return
            }
            push('/stores')
            closeDialogPopup()
            showSuccess(`"${store.name}" store has been suspended successfully`)
          }
        }
      ]
    })
  }

  public onSaveStoreDetailsClick = async (): Promise<void> => {
    await this.updateImageSquareBackround();
    const store = await this.storeApi.updateStore(this.store)
    if (!store) return
    this.setStore(store)
    this.storeBeforeChange = null
    this.setStoreDetailsEditMode(false)
  }

  public uploadProfileImage = async (userDetailsId) => {

    const {
      generalStore: {
        api: { uploadFileSigned }
      },
      imagesStore: {
        backgroundImage,
      }
    } = this.rootStore
    
    const imageUploadUrl = await uploadFileSigned(backgroundImage[0].file as File)
    this.rootStore.accountStore.updateUser(userDetailsId, false, imageUploadUrl.location)
  }

  public updateImageSquareBackround = async () => {
    const {
      generalStore: {
        api: { uploadFileSigned }
      },
      imagesStore: {
        squareImage,
        backgroundImage,
        getPositionSquare,
        getPositionBackground,
        getScaleSquare,
        getScaleBackground
      }
    } = this.rootStore
    if (!squareImage) {
      this.store.squareCrop = null
    }
    else if (squareImage[0].file === squareImage[0].link) {
      this.store.squareCrop = {
        url: squareImage[0].link,
        squareX: getPositionSquare.x,
        squareY: getPositionSquare.y,
        squareWidth: getScaleSquare
      }
    }
    else {
      const squreResult = await uploadFileSigned(squareImage[0].file as File)
      this.store.squareCrop = {
        url: squreResult.location,
        squareX: getPositionSquare.x,
        squareY: getPositionSquare.y,
        squareWidth: getScaleSquare
      }
    }
    if (!backgroundImage) {
      this.store.backgroundCrop = null
    }
    else if (backgroundImage[0].file === backgroundImage[0].link) {
      this.store.backgroundCrop = {
        url: backgroundImage[0].link,
        squareX: getPositionBackground.x,
        squareY: getPositionBackground.y,
        squareWidth: getScaleBackground
      }
    }
    else {
      const backgroundResult = await uploadFileSigned(backgroundImage[0].file as File)
      this.store.backgroundCrop = {
        url: backgroundResult.location,
        squareX: getPositionBackground.x,
        squareY: getPositionBackground.y,
        squareWidth: getScaleBackground
      }
    }
  }  

  public onCancelStoreDetailsEditingClick = (): void => {
    this.setStore(this.storeBeforeChange)
    this.storeBeforeChange = null
    this.setStoreDetailsEditMode(false)
  }

  public onStoreDetailsEditClick = (): void => {
    this.storeBeforeChange = this.store
    this.setStoreDetailsEditMode(true)
  }

  public setStoreAddressProps = (props: Partial<AddressDTO>): void => {
    if (!this.store) return
    const address = this.store.address || ({} as AddressDTO)
    this.setStoreProps({ address: { ...address, ...props } })
  }

  public setStorePaymentProps = (props: Partial<PaymentDetailsDTO>): void => {
    if (!this.store) return
    const paymentDetails = this.store.paymentDetails || ({} as PaymentDetailsDTO)
    this.setStoreProps({ paymentDetails : {...paymentDetails,...props}})
  }

  public updateImages = async (images: MediaContentDTO[]): Promise<void> => {
    const updatedImages = await this.storeApi.updateImage(this.storeId, images)
    if (!updatedImages) return
    this.setStoreProps({ images: updatedImages })
  }

  // COMPUTED
  public get storeId (): number {
    return this.store?.id || null
  }
}
